import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	shareReplay,
	switchMap
} from 'rxjs';

/**
 * Tracks which devices currently have a mode change (analog output mode or
 * conferencing operation mode) in flight.
 *
 * Consumers (e.g. routing `DeviceShapeFactory`) use `isDeviceModeChanging$` to
 * gate their rendering pipeline so the tile holds its current channel display
 * while the backend oscillates, then atomically swaps to the settled state.
 *
 * Producers (e.g. `RoutingService.setOutputMode`, `RoomSettingsService.setConferencingOperationMode`)
 * call `setDeviceModeChanging` before sending the mutation and
 * `clearDeviceModeChanging` in a `finalize` block after the mutation completes.
 */
@Injectable({ providedIn: 'root' })
export class DeviceModeChangingService {
	private readonly _devicesModeChanging = new Set<string>();
	private readonly _deviceModeChanging$ = new BehaviorSubject<Set<string>>(new Set());

	/**
	 * Signal that a mode change mutation is about to be sent for the given device.
	 * While flagged, downstream consumers will hold their current display.
	 */
	public setDeviceModeChanging(deviceId: string): void {
		this._devicesModeChanging.add(deviceId);
		this._deviceModeChanging$.next(new Set(this._devicesModeChanging));
	}

	/**
	 * Signal that the mode change mutation has completed (success or failure).
	 * Downstream consumers will resume processing subscription updates.
	 */
	public clearDeviceModeChanging(deviceId: string): void {
		this._devicesModeChanging.delete(deviceId);
		this._deviceModeChanging$.next(new Set(this._devicesModeChanging));
	}

	/**
	 * Returns an observable that emits true while the given device has a mode change in flight.
	 */
	public isDeviceModeChanging$(deviceId: string): Observable<boolean> {
		return this._deviceModeChanging$.pipe(
			map((set) => set.has(deviceId)),
			distinctUntilChanged()
		);
	}

	/**
	 * Create a stable stream that suppresses emissions during mode change
	 * transients for a given device.
	 */
	public getStabilizedDevice$<T>(
		deviceId$: Observable<string>,
		getSource: (deviceId: string) => Observable<T>
	): Observable<T> {
		return deviceId$.pipe(
			filter((id) => !!id),
			switchMap((id) =>
				combineLatest([getSource(id), this.isDeviceModeChanging$(id)]).pipe(
					filter(([, isChanging]) => !isChanging),
					map(([value]) => value)
				)
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}
}
